import Head from "next/head";
import React from "react";
import { AppProps } from "next/app";
import { ErrorBoundary } from "@sentry/nextjs";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { lato, raleway } from "@/fonts";
import "@/styles/globals.scss";
import NetworkStatusNotification from "@/components/Notification";
import Page from "@/hocs/page";
import RQProviders from "@/react-query";
import { ErrorScreen, onError, onReset } from "@/components/ErrorScreen";
import { I18nextProvider } from "@/locale/client-config";

// _app.tsx is used for customizing the root component of the application.
// It allows you to modify the initial props that are sent to all pages and add global CSS styles.
// You can think of it as the wrapper component for all your pages.
// When a page is rendered, it will be wrapped in the _app.tsx component.
export default function App(props: AppProps) {
  const [shouldStart, setShouldStart] = React.useState<boolean>(false);

  // https://github.com/mswjs/msw/issues/73
  React.useEffect(() => {
    if (process.env.NEXT_PUBLIC_API_MOCKING === "enabled") {
      import("../mocks").then(({
        initMocks
      }) => {
        initMocks().then(() => setShouldStart(true));
      });
    } else {
      setShouldStart(true);
    }
  }, []);
  if (!shouldStart) {
    return null;
  }
  return <ErrorBoundary onReset={onReset} onError={onError} fallback={<ErrorScreen resetError={onReset} />}>
      <div className={`${lato.variable} ${raleway.variable} regular`}>
        <RQProviders>
          <I18nextProvider>
            <Head>
              <title>Experteer</title>
              <meta name="google" content="notranslate" />
              <meta name="viewport" content="width=device-width, initial-scale=1" />
              <meta name="theme-color" content="#ffffff" key="theme-color" />
              <meta name="mobile-web-app-capable" content="yes" key="apple-status-bar-1" />
              <meta name="apple-mobile-web-app-status-bar-style" content="default" key="apple-status-bar-2" />
              <meta name="description" content="Find premium jobs for executives & network with Headhunters. Join now!" />
            </Head>
            <NetworkStatusNotification />
            <Page {...props} />
          </I18nextProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </RQProviders>
      </div>
    </ErrorBoundary>;
}